import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { workflowQuery } from "@/types/workflow";

export interface WorkflowRailItem {
  id: string;
  initial_goal: string | null;
  workflow_json: {
    name?: string;
    objective?: string;
    steps?: unknown[];
  } | null;
  cadence: string | null;
  status: string;
  started_at: string;
  completed_at: string | null;
  turn_count: number;
}

export interface WorkflowRailData {
  liveWorkflows: WorkflowRailItem[];
  inProgressWorkflows: WorkflowRailItem[];
  totalCount: number;
}

const EMPTY: WorkflowRailData = {
  liveWorkflows: [],
  inProgressWorkflows: [],
  totalCount: 0,
};

export function useWorkflowRail(userId?: string) {
  const [data, setData] = useState<WorkflowRailData>(EMPTY);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!userId) return;

    let cancelled = false;

    const fetch = async () => {
      setLoading(true);
      try {
        const [liveRes, inProgressRes, totalRes] = await Promise.all([
          workflowQuery<WorkflowRailItem>(supabase)
            .select("id, initial_goal, workflow_json, cadence, status, started_at, completed_at, turn_count")
            .eq("user_id", userId)
            .eq("status", "completed")
            .eq("is_archived", "false")
            .in("cadence", ["recurring", "perpetual"])
            .not("workflow_json", "is", null)
            .order("completed_at", { ascending: false })
            .limit(50),
          workflowQuery<WorkflowRailItem>(supabase)
            .select("id, initial_goal, workflow_json, cadence, status, started_at, completed_at, turn_count")
            .eq("user_id", userId)
            .eq("status", "in_progress")
            .eq("is_archived", "false")
            .order("started_at", { ascending: false })
            .limit(50),
          workflowQuery<WorkflowRailItem>(supabase)
            .select("id", { count: "exact", head: true })
            .eq("user_id", userId)
            .eq("is_archived", "false"),
        ]);

        if (cancelled) return;

        if (liveRes.error) console.error("Error fetching live workflows:", liveRes.error);
        if (inProgressRes.error) console.error("Error fetching in-progress workflows:", inProgressRes.error);
        if (totalRes.error) console.error("Error fetching total workflows:", totalRes.error);

        setData({
          liveWorkflows: liveRes.data || [],
          inProgressWorkflows: inProgressRes.data || [],
          totalCount: totalRes.count ?? 0,
        });
      } catch (err) {
        console.error("Error fetching workflow rail:", err);
        if (!cancelled) setData(EMPTY);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetch();
    return () => { cancelled = true; };
  }, [userId]);

  return { ...data, loading };
}
